import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ContentCard from "../components/ContentCard";
import { getContents } from "../api/contentApi";

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[ي]/g, "ی")
    .replace(/[ك]/g, "ک")
    .trim();

function NewsPage() {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") || "all");

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError("");

        const contents = await getContents("news");
        setNewsList(contents);
      } catch (err) {
        setError(err.message || "خطا در دریافت اخبار");
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  useEffect(() => {
    setSearchTerm(searchParams.get("search") || "");
    setActiveCategory(searchParams.get("category") || "all");
  }, [searchParams]);

  const categories = useMemo(() => {
    return [
      ...new Set(
        newsList
          .map((item) => item.category)
          .filter(Boolean)
      ),
    ];
  }, [newsList]);

  const filteredNews = useMemo(() => {
    const normalizedSearch = normalizeText(searchTerm);

    return newsList.filter((item) => {
      const matchesCategory =
        activeCategory === "all" || item.category === activeCategory;

      const searchableText = normalizeText(
        [
          item.title,
          item.summary,
          item.category,
          Array.isArray(item.tags) ? item.tags.join(" ") : item.tags,
        ].join(" ")
      );

      const matchesSearch =
        !normalizedSearch || searchableText.includes(normalizedSearch);

      return matchesCategory && matchesSearch;
    });
  }, [newsList, searchTerm, activeCategory]);

  const updateFilters = (nextSearch, nextCategory) => {
    const params = new URLSearchParams();

    if (nextSearch?.trim()) params.set("search", nextSearch.trim());
    if (nextCategory && nextCategory !== "all") params.set("category", nextCategory);

    setSearchParams(params);
  };

  return (
    <main className="content-page">
      <section className="page-hero glass-card">
        <span className="hero-badge">اخبار فناوری و آموزش</span>
        <h2>آخرین اخبار فناوری</h2>
        <p>
          تازه‌ترین خبرهای حوزه تکنولوژی، هوش مصنوعی، امنیت سایبری و بازار کار
          فناوری را اینجا دنبال کنید.
        </p>
      </section>

      <section className="content-section content-section-filter-first">
        <div className="content-filter-bar glass-card">
          <div className="content-filter-search">
            <svg
              className="content-filter-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M16.5 16.5L21 21" />
            </svg>
            <input
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                updateFilters(event.target.value, activeCategory);
              }}
              placeholder="جستجو در اخبار..."
            />
          </div>

          <div className="content-filter-chips" aria-label="فیلتر دسته‌بندی اخبار">
            <button
              type="button"
              className={activeCategory === "all" ? "active" : ""}
              onClick={() => {
                setActiveCategory("all");
                updateFilters(searchTerm, "all");
              }}
            >
              همه
            </button>

            {categories.map((category) => (
              <button
                key={category}
                type="button"
                className={activeCategory === category ? "active" : ""}
                onClick={() => {
                  setActiveCategory(category);
                  updateFilters(searchTerm, category);
                }}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {loading && <div className="content-state glass-card">در حال دریافت اخبار...</div>}

        {!loading && error && (
          <div className="content-state content-state-error glass-card">{error}</div>
        )}

        {!loading && !error && filteredNews.length === 0 && (
          <div className="content-state glass-card">
            موردی با این فیلتر پیدا نشد.
          </div>
        )}

        {!loading && !error && filteredNews.length > 0 && (
          <div className="content-grid">
            {filteredNews.map((item, index) => (
              <ContentCard key={item._id || item.id} item={item} priority={index < 3} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

export default NewsPage;
